<template>
  <v-container fluid class="pa-0">
	<v-row>
	  <v-col cols="12">
		<v-card>
		  <v-card-title>
			<span class="headline">Integrazioni Personalizzate</span>
			<v-spacer></v-spacer>
			<v-btn color="primary" @click="openCreateDialog">
			  <v-icon left>mdi-plus</v-icon>
			  Nuova Integrazione
			</v-btn>
		  </v-card-title>

		  <v-card-text>
			<v-data-table
			  :headers="headers"
			  :items="integrations"
			  :loading="loading"
			  class="elevation-1"
			>
			  <template v-slot:[`item.active`]="{ item }">
				<v-switch
				  v-model="item.active"
				  @change="toggleIntegration(item)"
				  :loading="item.updating"
				></v-switch>
			  </template>

			  <template v-slot:[`item.actions`]="{ item }">
				<v-tooltip bottom>
				  <template v-slot:activator="{ on, attrs }">
					<v-btn small text @click="editIntegration(item)" v-bind="attrs" v-on="on">
					  <v-icon small>mdi-pencil</v-icon>
					</v-btn>
				  </template>
				  <span>Modifica integrazione</span>
				</v-tooltip>
				
				<v-tooltip bottom>
				  <template v-slot:activator="{ on, attrs }">
					<v-btn small text @click="runImport(item)" :disabled="!item.active" v-bind="attrs" v-on="on">
					  <v-icon small>mdi-sync</v-icon>
					</v-btn>
				  </template>
				  <span>{{ item.active ? 'Avvia importazione' : 'Integrazione disattivata' }}</span>
				</v-tooltip>
				
				<v-tooltip bottom>
				  <template v-slot:activator="{ on, attrs }">
					<v-btn small text @click="testConnection(item)" v-bind="attrs" v-on="on">
					  <v-icon small>mdi-connection</v-icon>
					</v-btn>
				  </template>
				  <span>Testa connessione database</span>
				</v-tooltip>
				
				<v-tooltip bottom>
				  <template v-slot:activator="{ on, attrs }">
					<v-btn small text @click="duplicateIntegration(item)" v-bind="attrs" v-on="on">
					  <v-icon small>mdi-content-duplicate</v-icon>
					</v-btn>
				  </template>
				  <span>Duplica integrazione (disattivata per precauzione)</span>
				</v-tooltip>
				
				<v-tooltip bottom>
				  <template v-slot:activator="{ on, attrs }">
					<v-btn small text color="error" @click="deleteIntegration(item)" v-bind="attrs" v-on="on">
					  <v-icon small>mdi-delete</v-icon>
					</v-btn>
				  </template>
				  <span>Elimina integrazione</span>
				</v-tooltip>
			  </template>
			</v-data-table>
		  </v-card-text>
		</v-card>
	  </v-col>
	</v-row>

	<!-- Dialog per creazione/modifica integrazione -->
	<v-dialog v-model="dialog" max-width="900px" persistent>
	  <v-card>
		<v-card-title>
		  <span class="headline">{{ isEditing ? 'Modifica' : 'Nuova' }} Integrazione</span>
		</v-card-title>

		<v-card-text>
		  <v-stepper v-model="step" vertical>
			<!-- Step 1: Informazioni base -->
			<v-stepper-step :complete="step > 1" step="1">
			  Informazioni Base
			</v-stepper-step>
			<v-stepper-content step="1">
			  <v-form ref="basicForm">
				<v-text-field
				  v-model="currentIntegration.name"
				  label="Nome Integrazione"
				  required
				  :rules="[v => !!v || 'Nome richiesto']"
				></v-text-field>
				
				<v-select
				  v-model="currentIntegration.entity_type"
				  :items="entityTypeOptions"
				  label="Tipo di Entità"
				  required
				  :rules="[v => !!v || 'Tipo entità richiesto']"
				></v-select>
			  </v-form>
			  <v-btn color="primary" @click="step = 2">Continua</v-btn>
			</v-stepper-content>

			<!-- Step 2: Connessione database -->
			<v-stepper-step :complete="step > 2" step="2">
			  Connessione Database
			</v-stepper-step>
			<v-stepper-content step="2">
			  <v-form ref="connectionForm">
				<v-select
				  v-model="currentIntegration.connection.driver"
				  :items="databaseDriverOptions"
				  label="Tipo Database"
				  required
				></v-select>
				
				<v-text-field
				  v-model="currentIntegration.connection.host"
				  label="Host"
				  required
				></v-text-field>
				
				<v-text-field
				  v-model="currentIntegration.connection.port"
				  label="Porta"
				  type="number"
				></v-text-field>
				
				<v-text-field
				  v-model="currentIntegration.connection.database"
				  label="Nome Database"
				  required
				></v-text-field>
				
				<v-text-field
				  v-model="currentIntegration.connection.username"
				  label="Username"
				  required
				></v-text-field>
				
				<v-text-field
				  v-model="currentIntegration.connection.password"
				  label="Password"
				  type="password"
				  required
				></v-text-field>
				
				<v-text-field
				  v-model="currentIntegration.table"
				  label="Nome Tabella"
				  required
				></v-text-field>
			  </v-form>
			  
			  <v-btn text @click="step = 1">Indietro</v-btn>
			  <v-btn color="primary" @click="testAndContinue" :loading="testing">
				Testa Connessione e Continua
			  </v-btn>
			</v-stepper-content>

			<!-- Step 3: Mapping campi -->
			<v-stepper-step :complete="step > 3" step="3">
			  Mapping Campi
			</v-stepper-step>
			<v-stepper-content step="3">
			  <v-alert v-if="tableInfo" type="info" dense>
				Tabella: {{ currentIntegration.table }} - {{ tableInfo.total_records }} record trovati
			  </v-alert>
			  
			  <!-- Debug info -->
			  <v-alert v-if="availableColumns.length === 0" type="warning" dense>
				Nessuna colonna disponibile dal database esterno
			  </v-alert>
			  
			  <v-alert v-if="Object.keys(fieldMappingSuggestions).length === 0 && availableColumns.length > 0" type="warning" dense>
				Campi di sistema non caricati. Entity type: {{ currentIntegration.entity_type }}
				<br>Colonne disponibili: {{ availableColumns.map(c => c.value).join(', ') }}
			  </v-alert>
			  
			  <v-row v-if="availableColumns.length > 0">
				<v-col cols="6">
				  <h4>Campi Sistema</h4>
				</v-col>
				<v-col cols="6">
				  <h4>Campi Database Esterno</h4>
				</v-col>
			  </v-row>
			  
			  <v-row v-for="(label, field) in fieldMappingSuggestions" :key="field">
				<v-col cols="4">
				  <v-text-field
					:value="label"
					readonly
					dense
					:hint="requiredFields.includes(field) ? 'Campo obbligatorio' : ''"
					persistent-hint
				  ></v-text-field>
				</v-col>
				<v-col cols="4" class="d-flex align-center justify-center">
				  <v-btn-toggle
					v-model="currentMappingTypes[field]"
					mandatory
					dense
					@change="onMappingTypeChange(field, $event)"
				  >
					<v-btn value="simple" small>
					  <v-icon small>mdi-arrow-right</v-icon>
					</v-btn>
					<v-btn value="complex" small>
					  <v-icon small>mdi-cog</v-icon>
					</v-btn>
					<v-btn value="fixed" small>
					  <v-icon small>mdi-lock</v-icon>
					</v-btn>
					<v-btn value="relation" small v-if="relationMappings[field]">
					  <v-icon small>mdi-link</v-icon>
					</v-btn>
				  </v-btn-toggle>
				</v-col>
				<v-col cols="4">
				  <!-- Simple mapping -->
				  <v-select
					v-if="getMappingType(field) === 'simple'"
					v-model="currentIntegration.mapping[field]"
					:items="availableColumns"
					label="Seleziona campo"
					clearable
					dense
					:rules="requiredFields.includes(field) ? [v => !!v || 'Campo obbligatorio'] : []"
				  ></v-select>
				  
				  <!-- Fixed value -->
				  <v-text-field
					v-else-if="getMappingType(field) === 'fixed'"
					:value="getFixedValue(field)"
					@input="(value) => setFixedValue(field, value)"
					label="Valore fisso"
					dense
					:rules="requiredFields.includes(field) ? [v => !!v || 'Campo obbligatorio'] : []"
				  ></v-text-field>
				  
				  <!-- Complex mapping -->
				  <v-btn
					v-else-if="getMappingType(field) === 'complex'"
					@click="openComplexMappingDialog(field)"
					outlined
					dense
					block
				  >
					Configura mapping complesso
				  </v-btn>
				  
				  <!-- Relation mapping -->
				  <v-btn
					v-else-if="getMappingType(field) === 'relation'"
					@click="openRelationMappingDialog(field)"
					outlined
					dense
					block
					color="info"
				  >
					Configura relazione
				  </v-btn>
				</v-col>
			  </v-row>
			  
			  <v-btn text @click="step = 2">Indietro</v-btn>
			  <v-btn color="primary" @click="step = 4">Continua</v-btn>
			</v-stepper-content>

			<!-- Step 4: Configurazioni avanzate -->
			<v-stepper-step :complete="step > 4" step="4">
			  Configurazioni Avanzate
			</v-stepper-step>
			<v-stepper-content step="4">
			  <h4>Filtri (Opzionale)</h4>
			  <v-row v-for="(filter, index) in currentIntegration.filters" :key="index">
				<v-col cols="4">
				  <v-select
					v-model="filter.field"
					:items="availableColumns"
					label="Campo"
				  ></v-select>
				</v-col>
				<v-col cols="3">
				  <v-select
					v-model="filter.operator"
					:items="[
					  { text: 'Uguale', value: '=' },
					  { text: 'Diverso', value: '!=' },
					  { text: 'Maggiore', value: '>' },
					  { text: 'Minore', value: '<' }
					]"
					label="Operatore"
				  ></v-select>
				</v-col>
				<v-col cols="4">
				  <v-text-field
					v-model="filter.value"
					label="Valore"
				  ></v-text-field>
				</v-col>
				<v-col cols="1">
				  <v-btn icon @click="removeFilter(index)">
					<v-icon>mdi-delete</v-icon>
				  </v-btn>
				</v-col>
			  </v-row>
			  
			  <v-btn text @click="addFilter">
				<v-icon left>mdi-plus</v-icon>
				Aggiungi Filtro
			  </v-btn>
			  
			  <v-divider class="my-4"></v-divider>
			  
			  <h4>Sincronizzazione Incrementale</h4>
			  <v-select
				v-model="currentIntegration.date_field"
				:items="availableColumns"
				label="Campo Data per Sync Incrementale (Opzionale)"
				clearable
				hint="Se specificato, verranno importati solo i record modificati dall'ultimo sync"
				persistent-hint
			  ></v-select>
			  
			  <v-switch
				v-model="currentIntegration.active"
				label="Integrazione Attiva"
			  ></v-switch>
			  
			  <v-btn text @click="step = 3">Indietro</v-btn>
			  <v-btn color="success" @click="saveIntegration" :loading="saving">
				{{ isEditing ? 'Aggiorna' : 'Salva' }} Integrazione
			  </v-btn>
			</v-stepper-content>
		  </v-stepper>
		</v-card-text>

		<v-card-actions>
		  <v-spacer></v-spacer>
		  <v-btn text @click="closeDialog">Annulla</v-btn>
		</v-card-actions>
	  </v-card>
	</v-dialog>

	<!-- Dialog test connessione -->
	<v-dialog v-model="testDialog" max-width="600px">
	  <v-card>
		<v-card-title>Test Connessione</v-card-title>
		<v-card-text>
		  <v-alert :type="testResult.success ? 'success' : 'error'" v-if="testResult">
			{{ testResult.message || (testResult.success ? 'Connessione riuscita!' : 'Connessione fallita') }}
		  </v-alert>
		  <div v-if="testResult && testResult.success && tableInfo">
			<p><strong>Tabella:</strong> {{ currentIntegration.table }}</p>
			<p><strong>Record totali:</strong> {{ tableInfo.total_records }}</p>
			<p><strong>Colonne disponibili:</strong></p>
			<v-chip-group>
			  <v-chip v-for="column in tableInfo.columns" :key="column" small>
				{{ column }}
			  </v-chip>
			</v-chip-group>
		  </div>
		</v-card-text>
		<v-card-actions>
		  <v-spacer></v-spacer>
		  <v-btn text @click="testDialog = false">Chiudi</v-btn>
		</v-card-actions>
	  </v-card>
	</v-dialog>

	<!-- Dialog mapping complesso -->
	<v-dialog v-model="complexMappingDialog" max-width="800px">
	  <v-card>
		<v-card-title>
		  Configurazione Mapping Complesso
		  <v-spacer></v-spacer>
		  <span class="subtitle-1 grey--text">Campo: {{ currentComplexField }}</span>
		</v-card-title>

		<v-card-text>
		  <v-select
			v-model="complexMappingConfig.type"
			:items="complexMappingTypes"
			label="Tipo di Mapping"
			@change="onComplexTypeChange"
		  ></v-select>

		  <!-- Concatenazione -->
		  <div v-if="complexMappingConfig.type === 'concat'">
			<h4>Concatenazione Campi</h4>
			<v-row v-for="(field, index) in complexMappingConfig.fields" :key="index">
			  <v-col cols="8">
				<v-select
				  v-model="complexMappingConfig.fields[index]"
				  :items="availableColumns"
				  label="Campo"
				></v-select>
			  </v-col>
			  <v-col cols="3">
				<v-btn icon @click="removeComplexField(index)" :disabled="complexMappingConfig.fields.length <= 1">
				  <v-icon>mdi-delete</v-icon>
				</v-btn>
			  </v-col>
			</v-row>
			<v-btn text @click="addComplexField">
			  <v-icon left>mdi-plus</v-icon>
			  Aggiungi Campo
			</v-btn>
			
			<v-text-field
			  v-model="complexMappingConfig.separator"
			  label="Separatore"
			  hint="Es: spazio, virgola, trattino"
			  persistent-hint
			></v-text-field>
		  </div>

		  <!-- Valore di Default -->
		  <div v-if="complexMappingConfig.type === 'default'">
			<h4>Campo con Valore di Default</h4>
			<v-select
			  v-model="complexMappingConfig.field"
			  :items="availableColumns"
			  label="Campo Principale"
			></v-select>
			
			<v-text-field
			  v-model="complexMappingConfig.default_value"
			  label="Valore di Default"
			  hint="Usato se il campo principale è vuoto"
			  persistent-hint
			></v-text-field>
		  </div>

		  <!-- Trasformazione -->
		  <div v-if="complexMappingConfig.type === 'transform'">
			<h4>Trasformazione Campo</h4>
			<v-select
			  v-model="complexMappingConfig.field"
			  :items="availableColumns"
			  label="Campo da Trasformare"
			></v-select>
			
			<v-select
			  v-model="complexMappingConfig.transform.method"
			  :items="transformMethods"
			  label="Metodo di Trasformazione"
			  @change="onTransformMethodChange"
			></v-select>

			<!-- Transform specifiche -->
			<div v-if="complexMappingConfig.transform.method === 'date_format'">
			  <v-text-field
				v-model="complexMappingConfig.transform.from_format"
				label="Formato Sorgente"
				hint="Es: d/m/Y, Y-m-d H:i:s"
				persistent-hint
			  ></v-text-field>
			  <v-text-field
				v-model="complexMappingConfig.transform.to_format"
				label="Formato Destinazione"
				hint="Es: Y-m-d, d/m/Y H:i:s"
				persistent-hint
			  ></v-text-field>
			</div>

			<div v-if="complexMappingConfig.transform.method === 'map_values'">
			  <h5>Mappatura Valori</h5>
			  <v-row v-for="(value, key, index) in complexMappingConfig.transform.map" :key="index">
				<v-col cols="5">
				  <v-text-field
					:value="key"
					@input="(value) => updateMapKey(key, value)"
					label="Valore Sorgente"
				  ></v-text-field>
				</v-col>
				<v-col cols="5">
				  <v-text-field
					v-model="complexMappingConfig.transform.map[key]"
					label="Valore Destinazione"
				  ></v-text-field>
				</v-col>
				<v-col cols="2">
				  <v-btn icon @click="removeMapEntry(key)">
					<v-icon>mdi-delete</v-icon>
				  </v-btn>
				</v-col>
			  </v-row>
			  
			  <v-btn text @click="addMapEntry">
				<v-icon left>mdi-plus</v-icon>
				Aggiungi Mappatura
			  </v-btn>
			  
			  <v-text-field
				v-model="complexMappingConfig.transform.default"
				label="Valore Default"
				hint="Usato se il valore non è nella mappatura"
				persistent-hint
			  ></v-text-field>
			</div>

			<div v-if="complexMappingConfig.transform.method === 'multiply'">
			  <v-text-field
				v-model.number="complexMappingConfig.transform.factor"
				label="Fattore di Moltiplicazione"
				type="number"
				step="0.01"
			  ></v-text-field>
			</div>
		  </div>
		</v-card-text>

		<v-card-actions>
		  <v-spacer></v-spacer>
		  <v-btn text @click="closeComplexMappingDialog">Annulla</v-btn>
		  <v-btn color="primary" @click="saveComplexMapping">Salva</v-btn>
		</v-card-actions>
	  </v-card>
	</v-dialog>

	<!-- Dialog mapping relazione -->
	<v-dialog v-model="relationMappingDialog" max-width="600px">
	  <v-card>
		<v-card-title>
		  Configurazione Mapping Relazione
		  <v-spacer></v-spacer>
		  <span class="subtitle-1 grey--text">Campo: {{ currentRelationField }}</span>
		</v-card-title>

		<v-card-text>
		  <v-form>
			<v-select
			  v-model="relationMappingConfig.external_field"
			  :items="availableColumns"
			  label="Campo Database Esterno"
			  hint="Seleziona il campo della tabella esterna che contiene il codice/nome della relazione"
			  persistent-hint
			  required
			></v-select>

			<v-text-field
			  v-model="relationMappingConfig.search_field"
			  label="Campo di Ricerca Primario"
			  hint="Campo nel sistema interno per la ricerca (default: code)"
			  persistent-hint
			></v-text-field>

			<v-text-field
			  v-model="relationMappingConfig.fallback_field"
			  label="Campo di Ricerca Alternativo"
			  hint="Campo alternativo per la ricerca se il primario non trova risultati (default: name)"
			  persistent-hint
			></v-text-field>

			<v-switch
			  v-model="relationMappingConfig.create_if_missing"
			  label="Crea record se mancante"
			  hint="Se attivato, crea automaticamente nuovi record di relazione se non trovati"
			  persistent-hint
			></v-switch>

			<v-divider class="my-4"></v-divider>

			<h4>Anteprima Record Disponibili</h4>
			<v-simple-table dense>
			  <template v-slot:default>
				<thead>
				  <tr>
					<th>ID</th>
					<th>Nome</th>
					<th>Codice</th>
				  </tr>
				</thead>
				<tbody>
				  <tr v-for="option in relationOptions.slice(0, 5)" :key="option.id">
					<td>{{ option.id }}</td>
					<td>{{ option.name }}</td>
					<td>{{ option.code || '-' }}</td>
				  </tr>
				</tbody>
			  </template>
			</v-simple-table>
			<p v-if="relationOptions.length > 5" class="caption mt-2">
			  ... e altri {{ relationOptions.length - 5 }} record
			</p>
		  </v-form>
		</v-card-text>

		<v-card-actions>
		  <v-spacer></v-spacer>
		  <v-btn text @click="closeRelationMappingDialog">Annulla</v-btn>
		  <v-btn color="primary" @click="saveRelationMapping">Salva</v-btn>
		</v-card-actions>
	  </v-card>
	</v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'CustomIntegrations',
  data() {
	return {
	  loading: false,
	  dialog: false,
	  testDialog: false,
	  complexMappingDialog: false,
	  relationMappingDialog: false,
	  step: 1,
	  isEditing: false,
	  testing: false,
	  saving: false,
	  integrations: [],
	  currentMappingTypes: {}, // Per gestire i v-btn-toggle
	  currentIntegration: {
		name: '',
		entity_type: '',
		connection: {
		  driver: 'mysql',
		  host: '',
		  port: 3306,
		  database: '',
		  username: '',
		  password: ''
		},
		table: '',
		mapping: {},
		filters: [],
		date_field: '',
		active: true
	  },
	  entityTypeOptions: [],
	  databaseDriverOptions: [],
	  fieldMappingSuggestions: {},
	  relationMappings: {},
	  requiredFields: [],
	  availableColumns: [],
	  tableInfo: null,
	  testResult: null,
	  currentComplexField: '',
	  currentRelationField: '',
	  relationMappingConfig: {
		external_field: '',
		search_field: 'code',
		fallback_field: 'name',
		create_if_missing: false,
		default_fields: {}
	  },
	  relationOptions: [],
	  complexMappingConfig: {
		type: 'concat',
		fields: [''],
		separator: ' ',
		field: '',
		default_value: '',
		transform: {
		  method: 'uppercase',
		  from_format: '',
		  to_format: '',
		  map: {},
		  default: '',
		  factor: 1
		}
	  },
	  complexMappingTypes: [
		{ text: 'Concatenazione', value: 'concat' },
		{ text: 'Valore di Default', value: 'default' },
		{ text: 'Trasformazione', value: 'transform' }
	  ],
	  transformMethods: [
		{ text: 'Maiuscolo', value: 'uppercase' },
		{ text: 'Minuscolo', value: 'lowercase' },
		{ text: 'Formato Data', value: 'date_format' },
		{ text: 'Mappatura Valori', value: 'map_values' },
		{ text: 'Moltiplicazione', value: 'multiply' }
	  ],
	  headers: [
		{ text: 'Nome', value: 'name' },
		{ text: 'Tipo Entità', value: 'entity_type' },
		{ text: 'Database', value: 'connection.database' },
		{ text: 'Tabella', value: 'table' },
		{ text: 'Attiva', value: 'active' },
		{ text: 'Azioni', value: 'actions', sortable: false }
	  ]
	}
  },
  
  created() {
	this.loadIntegrations()
  },

  watch: {
	'currentIntegration.mapping': {
	  handler(newMapping) {
		// Sincronizza currentMappingTypes con le mappature effettive
		this.syncMappingTypes()
	  },
	  deep: true
	},
	
	'currentIntegration.entity_type': {
	  handler(newEntityType) {
		if (newEntityType) {
		  this.loadFieldMappings()
		}
	  }
	},
	
	fieldMappingSuggestions: {
	  handler() {
		this.syncMappingTypes()
	  }
	}
  },
  
  methods: {
	async loadIntegrations() {
	  this.loading = true
	  try {
		const response = await this.axios.get('custom-integrations-settings')
		this.integrations = Object.values(response.data.data || {})
		this.entityTypeOptions = Object.entries(response.data.entity_types || {}).map(([value, text]) => ({ text, value }))
		this.databaseDriverOptions = Object.entries(response.data.database_drivers || {}).map(([value, text]) => ({ text, value }))
	  } catch (error) {
		this.$store.commit('setSnackbar', {
		  text: 'Errore nel caricamento delle integrazioni',
		  color: 'error',
		  value: true
		})
	  } finally {
		this.loading = false
	  }
	},
	
	openCreateDialog() {
	  this.isEditing = false
	  this.step = 1
	  this.resetCurrentIntegration()
	  this.dialog = true
	},
	
	editIntegration(integration) {
	  this.isEditing = true
	  this.step = 1
	  this.currentIntegration = JSON.parse(JSON.stringify(integration))
	  this.dialog = true
	  
	  // Carica le field mappings se l'entity type è già selezionato
	  if (this.currentIntegration.entity_type) {
		this.loadFieldMappings()
	  }
	},
	
	resetCurrentIntegration() {
	  this.currentIntegration = {
		name: '',
		entity_type: '',
		connection: {
		  driver: 'mysql',
		  host: '',
		  port: 3306,
		  database: '',
		  username: '',
		  password: ''
		},
		table: '',
		mapping: {},
		filters: [],
		date_field: null,
		active: true
	  }
	  this.availableColumns = []
	  this.tableInfo = null
	  this.fieldMappingSuggestions = {}
	  this.relationMappings = {}
	  this.requiredFields = []
	  this.currentMappingTypes = {}
	},
	
	closeDialog() {
	  this.dialog = false
	  this.isEditing = false
	  this.resetCurrentIntegration()
	},
	
	async testAndContinue() {
	  this.testing = true
	  try {
		const response = await this.axios.post('custom-integrations/table-info', {
		  connection: this.currentIntegration.connection,
		  table: this.currentIntegration.table
		})
		
		
		if (response.data.success) {
		  this.tableInfo = response.data
		  this.availableColumns = response.data.columns.map(col => ({ text: col, value: col }))
		  await this.loadFieldMappings()
		  this.step = 3
		} else {
		  this.$store.commit('setSnackbar', {
			text: response.data.message || 'Errore nel test della connessione',
			color: 'error',
			value: true
		  })
		}
	  } catch (error) {
		console.error('Error in testAndContinue:', error)
		this.$store.commit('setSnackbar', {
		  text: 'Errore nel test della connessione',
		  color: 'error',
		  value: true
		})
	  } finally {
		this.testing = false
	  }
	},
	
	async loadFieldMappings() {
	  try {
		const response = await this.axios.get(`custom-integrations/mapping-suggestions/${this.currentIntegration.entity_type}`)
		
		this.fieldMappingSuggestions = response.data.mappings || {}
		this.relationMappings = response.data.relations || {}
		this.requiredFields = response.data.required_fields || []
		
		// Sincronizza i tipi di mapping dopo aver caricato i campi
		this.$nextTick(() => {
		  this.syncMappingTypes()
		})
	  } catch (error) {
		console.error('Error loading field mappings:', error)
		this.$store.commit('setSnackbar', {
		  text: 'Errore nel caricamento dei campi di sistema',
		  color: 'error',
		  value: true
		})
	  }
	},
	
	addFilter() {
	  this.currentIntegration.filters.push({
		field: '',
		operator: '=',
		value: ''
	  })
	},
	
	removeFilter(index) {
	  this.currentIntegration.filters.splice(index, 1)
	},
	
	async saveIntegration() {
	  this.saving = true
	  try {
		// Include the ID when editing to ensure update instead of create
		const integrationData = { ...this.currentIntegration }
		if (this.isEditing && integrationData.id) {
		  // Ensure ID is present for updates
		} else {
		  // Remove ID for new integrations to let backend generate one
		  delete integrationData.id
		}
		
		const response = await this.axios.post('custom-integrations-settings', integrationData)
		
		if (response.data.success) {
		  this.$store.commit('setSnackbar', {
			text: this.isEditing ? 'Integrazione aggiornata con successo' : 'Integrazione creata con successo',
			color: 'success',
			value: true
		  })
		  this.closeDialog()
		  this.loadIntegrations()
		}
	  } catch (error) {
		console.error('Error saving integration:', error)
		this.$store.commit('setSnackbar', {
		  text: 'Errore nel salvataggio dell\'integrazione',
		  color: 'error',
		  value: true
		})
	  } finally {
		this.saving = false
	  }
	},
	
	async toggleIntegration(integration) {
	  integration.updating = true
	  try {
		await this.axios.post('custom-integrations-settings', integration)
		this.$store.commit('setSnackbar', {
		  text: `Integrazione ${integration.active ? 'attivata' : 'disattivata'}`,
		  color: 'success',
		  value: true
		})
	  } catch (error) {
		// Revert the change
		integration.active = !integration.active
		this.$store.commit('setSnackbar', {
		  text: 'Errore nell\'aggiornamento dell\'integrazione',
		  color: 'error',
		  value: true
		})
	  } finally {
		integration.updating = false
	  }
	},
	
	async runImport(integration) {
	  try {
		const response = await this.axios.post('custom-integrations/run-import', {
		  integration_id: integration.id
		})
		
		this.$store.commit('setSnackbar', {
		  text: 'Importazione avviata con successo',
		  color: 'success',
		  value: true
		})
	  } catch (error) {
		this.$store.commit('setSnackbar', {
		  text: 'Errore nell\'avvio dell\'importazione',
		  color: 'error',
		  value: true
		})
	  }
	},
	
	async testConnection(integration) {
	  this.testing = true
	  try {
		const response = await this.axios.post('custom-integrations/test-connection', {
		  connection: integration.connection,
		  table: integration.table
		})
		
		this.testResult = response.data
		if (response.data.success) {
		  const tableResponse = await this.axios.post('custom-integrations/table-info', {
			connection: integration.connection,
			table: integration.table
		  })
		  this.tableInfo = tableResponse.data
		  this.currentIntegration = integration
		}
		this.testDialog = true
	  } catch (error) {
		this.testResult = {
		  success: false,
		  message: 'Errore nel test della connessione'
		}
		this.testDialog = true
	  } finally {
		this.testing = false
	  }
	},
	
	async deleteIntegration(integration) {
	  if (confirm('Sei sicuro di voler eliminare questa integrazione?')) {
		try {
		  await this.axios.delete(`custom-integrations/${integration.id}`)
		  this.$store.commit('setSnackbar', {
			text: 'Integrazione eliminata con successo',
			color: 'success',
			value: true
		  })
		  this.loadIntegrations()
		} catch (error) {
		  this.$store.commit('setSnackbar', {
			text: 'Errore nell\'eliminazione dell\'integrazione',
			color: 'error',
			value: true
		  })
		}
	  }
	},
	
	async duplicateIntegration(integration) {
	  if (confirm('Vuoi duplicare questa integrazione? Sarà creata disattivata per precauzione.')) {
		try {
		  const response = await this.axios.post('custom-integrations/duplicate', {
			integration_id: integration.id
		  })
		  
		  this.$store.commit('setSnackbar', {
			text: 'Integrazione duplicata con successo',
			color: 'success',
			value: true
		  })
		  this.loadIntegrations()
		} catch (error) {
		  this.$store.commit('setSnackbar', {
			text: 'Errore nella duplicazione dell\'integrazione',
			color: 'error',
			value: true
		  })
		}
	  }
	},
	
	// Complex mapping methods
	getMappingType(field) {
	  const mapping = this.currentIntegration.mapping[field]
	  if (typeof mapping === 'object' && mapping !== null) {
		if (mapping.type === 'relation') {
		  return 'relation'
		}
		return 'complex'
	  } else if (typeof mapping === 'string' && mapping.startsWith('FIXED:')) {
		return 'fixed'
	  } else {
		return 'simple'
	  }
	},
	
	// Sync mapping types for v-btn-toggle
	syncMappingTypes() {
	  const newMappingTypes = {}
	  Object.keys(this.fieldMappingSuggestions).forEach(field => {
		newMappingTypes[field] = this.getMappingType(field)
	  })
	  this.currentMappingTypes = newMappingTypes
	},
	
	onMappingTypeChange(field, type) {	  
	  if (type === 'simple') {
		this.$set(this.currentIntegration.mapping, field, '')
	  } else if (type === 'fixed') {
		this.$set(this.currentIntegration.mapping, field, 'FIXED:')
	  } else if (type === 'complex') {
		this.$set(this.currentIntegration.mapping, field, {
		  type: 'concat',
		  fields: [''],
		  separator: ' '
		})
	  } else if (type === 'relation') {
		const relationConfig = this.relationMappings[field]
		this.$set(this.currentIntegration.mapping, field, {
		  type: 'relation',
		  relation_model: relationConfig?.relation_model || '',
		  external_field: '',
		  search_field: 'code',
		  fallback_field: 'name',
		  create_if_missing: false
		})
	  }
	  
	  // Aggiorna esplicitamente il mapping type
	  this.$set(this.currentMappingTypes, field, type)
	},
	
	getFixedValue(field) {
	  const mapping = this.currentIntegration.mapping[field]
	  if (typeof mapping === 'string' && mapping.startsWith('FIXED:')) {
		return mapping.substring(6)
	  }
	  return ''
	},
	
	setFixedValue(field, value) {
	  this.currentIntegration.mapping[field] = 'FIXED:' + value
	},
	
	openComplexMappingDialog(field) {
	  this.currentComplexField = field
	  const mapping = this.currentIntegration.mapping[field]
	  
	  if (typeof mapping === 'object' && mapping !== null) {
		this.complexMappingConfig = JSON.parse(JSON.stringify(mapping))
	  } else {
		this.resetComplexMappingConfig()
	  }
	  
	  this.complexMappingDialog = true
	},
	
	closeComplexMappingDialog() {
	  this.complexMappingDialog = false
	  this.currentComplexField = ''
	  this.resetComplexMappingConfig()
	},
	
	resetComplexMappingConfig() {
	  this.complexMappingConfig = {
		type: 'concat',
		fields: [''],
		separator: ' ',
		field: '',
		default_value: '',
		transform: {
		  method: 'uppercase',
		  from_format: '',
		  to_format: '',
		  map: {},
		  default: '',
		  factor: 1
		}
	  }
	},
	
	onComplexTypeChange() {
	  // Reset specific configurations when type changes
	  if (this.complexMappingConfig.type === 'concat') {
		this.complexMappingConfig.fields = ['']
		this.complexMappingConfig.separator = ' '
	  } else if (this.complexMappingConfig.type === 'default') {
		this.complexMappingConfig.field = ''
		this.complexMappingConfig.default_value = ''
	  } else if (this.complexMappingConfig.type === 'transform') {
		this.complexMappingConfig.field = ''
		this.complexMappingConfig.transform = {
		  method: 'uppercase',
		  from_format: '',
		  to_format: '',
		  map: {},
		  default: '',
		  factor: 1
		}
	  }
	},
	
	onTransformMethodChange() {
	  // Reset transform specific config when method changes
	  this.complexMappingConfig.transform = {
		method: this.complexMappingConfig.transform.method,
		from_format: '',
		to_format: '',
		map: {},
		default: '',
		factor: 1
	  }
	},
	
	addComplexField() {
	  this.complexMappingConfig.fields.push('')
	},
	
	removeComplexField(index) {
	  if (this.complexMappingConfig.fields.length > 1) {
		this.complexMappingConfig.fields.splice(index, 1)
	  }
	},
	
	addMapEntry() {
	  this.$set(this.complexMappingConfig.transform.map, '', '')
	},
	
	removeMapEntry(key) {
	  this.$delete(this.complexMappingConfig.transform.map, key)
	},
	
	updateMapKey(oldKey, newKey) {
	  if (oldKey !== newKey) {
		const value = this.complexMappingConfig.transform.map[oldKey]
		this.$delete(this.complexMappingConfig.transform.map, oldKey)
		this.$set(this.complexMappingConfig.transform.map, newKey, value)
	  }
	},
	
	saveComplexMapping() {
	  // Clean up the config based on type
	  let cleanConfig = { type: this.complexMappingConfig.type }
	  
	  if (this.complexMappingConfig.type === 'concat') {
		cleanConfig.fields = this.complexMappingConfig.fields.filter(f => f !== '')
		cleanConfig.separator = this.complexMappingConfig.separator
	  } else if (this.complexMappingConfig.type === 'default') {
		cleanConfig.field = this.complexMappingConfig.field
		cleanConfig.default_value = this.complexMappingConfig.default_value
	  } else if (this.complexMappingConfig.type === 'transform') {
		cleanConfig.field = this.complexMappingConfig.field
		cleanConfig.transform = this.complexMappingConfig.transform
	  }
	  
	  this.currentIntegration.mapping[this.currentComplexField] = cleanConfig
	  this.closeComplexMappingDialog()
	},
	
	// Relation mapping methods
	openRelationMappingDialog(field) {
	  this.currentRelationField = field
	  const mapping = this.currentIntegration.mapping[field]
	  
	  if (typeof mapping === 'object' && mapping !== null && mapping.type === 'relation') {
		this.relationMappingConfig = {
		  external_field: mapping.external_field || '',
		  search_field: mapping.search_field || 'code',
		  fallback_field: mapping.fallback_field || 'name',
		  create_if_missing: mapping.create_if_missing || false,
		  default_fields: mapping.default_fields || {}
		}
	  } else {
		this.resetRelationMappingConfig()
	  }
	  
	  this.loadRelationOptions()
	  this.relationMappingDialog = true
	},
	
	closeRelationMappingDialog() {
	  this.relationMappingDialog = false
	  this.currentRelationField = ''
	  this.resetRelationMappingConfig()
	},
	
	resetRelationMappingConfig() {
	  this.relationMappingConfig = {
		external_field: '',
		search_field: 'code',
		fallback_field: 'name',
		create_if_missing: false,
		default_fields: {}
	  }
	},
	
	async loadRelationOptions() {
	  if (!this.currentRelationField) return
	  
	  try {
		const response = await this.axios.get('custom-integrations/relation-options', {
		  params: {
			entity_type: this.currentIntegration.entity_type,
			relation_field: this.currentRelationField
		  }
		})
		
		this.relationOptions = response.data.relation_records || []
	  } catch (error) {
		console.error('Errore nel caricamento delle opzioni di relazione:', error)
		this.relationOptions = []
	  }
	},
	
	saveRelationMapping() {
	  const relationConfig = this.relationMappings[this.currentRelationField]
	  
	  const cleanConfig = {
		type: 'relation',
		relation_model: relationConfig?.relation_model || '',
		external_field: this.relationMappingConfig.external_field,
		search_field: this.relationMappingConfig.search_field,
		fallback_field: this.relationMappingConfig.fallback_field,
		create_if_missing: this.relationMappingConfig.create_if_missing
	  }
	  
	  if (Object.keys(this.relationMappingConfig.default_fields).length > 0) {
		cleanConfig.default_fields = this.relationMappingConfig.default_fields
	  }
	  
	  this.currentIntegration.mapping[this.currentRelationField] = cleanConfig
	  this.closeRelationMappingDialog()
	}
  }
}
</script>
